<script setup>
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref } from 'vue'
import { humanReadableTimestamp } from '@/composables/datetime'
import { useCamerasStore } from '@/store/cameras.js'
import { useCameraName } from '@/composables/cameraHelpers.ts'
import WYSIWYG from '@/components/WYSIWYG.vue'

const { t } = useI18n()

const props = defineProps({ incident: { type: Object, default: () => ({}) } })

const humanReadableCameras = ref('')

const humanReadableSpecificationType = computed(() => {
  const specification = props.incident.messagePayload.specification
  const type = specification.type
  return t(`incidents.barnDocumentation.specificationTypes.${type}`)
})

const humanReadableClassification = computed(() => {
  const classification = props.incident.messagePayload.classification
  return t(`incidents.barnDocumentation.classifications.${classification}`)
})

const newEventButtonText = computed(() => {
  return t('incidents.barnDocumentation.actions.finish')
})

const isTimestampIncident = computed(() => {
  return props.incident.state === 'closed' && props.incident.firstEventTimestamp === props.incident.lastEventTimestamp
})

const isGlobal = computed(() => {
  return props.incident.messagePayload.specification.location === 'global'
})

const wasCreatedAutomatically = computed(() => {
  return props.incident.triggeredBy.entityType !== 'account'
})

const hasRegions = computed(() => {
  if (isGlobal.value) return false
  return Object.keys(props.incident.messagePayload.specification.location).includes('regionSpecification')
})

const hasCameras = computed(() => {
  if (isGlobal.value) return false
  return Object.keys(props.incident.messagePayload.specification.location).includes('cameraId')
})

const humanReadableStatus = computed(() => {
  return t(`incidents.barnDocumentation.status.${props.incident.state}`)
})

const humanReadableRegions = computed(() => {
  if (!hasRegions.value) return null
  const regionSpecification = props.incident.messagePayload.specification.location.regionSpecification
  return regionSpecification.join(', ')
})

const isDescriptionEmpty = computed(() => {
  const hasDescription = Object.keys(props.incident.messagePayload).includes('description')
  return hasDescription && props.incident.messagePayload.description.length <= 7
})

const statusIcon = computed(() => {
  if (props.incident.state === 'closed') {
    return 'mdi-lock'
  }
  if (props.incident.state === 'open') {
    return 'mdi-lock-open-variant'
  }
  return 'mdi-information'
})

const classificationIcon = computed(() => {
  if (props.incident.messagePayload.classification === 'animalDistribution') {
    return 'mdi-dots-grid'
  }
  if (props.incident.messagePayload.classification === 'animals') {
    return 'mdi-paw'
  }
  if (props.incident.messagePayload.classification === 'barn') {
    return 'mdi-barn'
  }
  if (props.incident.messagePayload.classification === 'behaviour') {
    return 'mdi-walk'
  }
  if (props.incident.messagePayload.classification === 'climate') {
    return 'mdi-weather-partly-cloudy'
  }
  if (props.incident.messagePayload.classification === 'configChange') {
    return 'mdi-cog'
  }
  if (props.incident.messagePayload.classification === 'disease') {
    return 'mdi-hospital-box'
  }
  if (props.incident.messagePayload.classification === 'miscellaneous') {
    return 'mdi-dots-horizontal'
  }
  if (props.incident.messagePayload.classification === 'technical') {
    return 'mdi-tools'
  }

  return 'mdi-bookmark'
})

onMounted(async () => {
  if (hasCameras.value) {
    await useCamerasStore().ensureCamerasLoaded()
    const cameraNames = []
    for (const cameraId of props.incident.messagePayload.specification.location.cameraId) {
      cameraNames.push(useCameraName(cameraId))
    }
    humanReadableCameras.value = cameraNames.join(', ')
  }
})
</script>

<template>
  <v-layout-card>
    <div class="w-100 px-2 d-flex flex-row justify-space-between align-start flex-wrap">
      <div class="d-flex flex-column align-start">
        <v-card-title>
          <v-icon
            icon="mdi-file-document-edit"
            start
          />
          {{ humanReadableSpecificationType }}
        </v-card-title>
        <div class="mx-2 d-flex flex-row align-center ga-2 flex-wrap">
          <v-chip
            class="px-4"
            :prepend-icon="statusIcon"
            size="small"
            variant="flat"
          >
            {{ humanReadableStatus }}
          </v-chip>
          <v-chip
            v-if="props.incident.messagePayload.severity !== 'none'"
            class="px-4"
            :prepend-icon="classificationIcon"
            size="small"
            variant="tonal"
            color="primary"
          >
            {{ humanReadableClassification }}
          </v-chip>
          <v-chip
            class="px-4"
            prepend-icon="mdi-label"
            size="small"
            variant="tonal"
          >
            {{ humanReadableSpecificationType }}
          </v-chip>
        </div>
      </div>

      <v-card-subtitle v-if="incident.hidden === true">
        {{ $t('incidents.barnDocumentation.hidden') }}
      </v-card-subtitle>

      <div class="align-self-start d-flex flex-row align-center ga-2">
        <slot name="jsonButton" />
        <slot name="commentButton" />
        <slot name="assignButton" />
      </div>
    </div>

    <v-card-subtitle v-if="incident.state === 'created'">
      <v-alert
        type="warning"
        variant="flat"
        density="compact"
      >
        {{ $t('incidents.barnDocumentation.not_yet_finished') }}
      </v-alert>
    </v-card-subtitle>

    <v-card-text class="py-2">
      <v-list-item
        v-if="isTimestampIncident"
        :title="$t('incidents.barnDocumentation.titles.timestamp')"
        :subtitle="humanReadableTimestamp(incident.firstEventTimestamp)"
        prepend-icon="mdi-clock"
      />
      <v-list-item
        v-if="!isTimestampIncident"
        :title="$t('incidents.barnDocumentation.titles.startTimestamp')"
        :subtitle="humanReadableTimestamp(incident.firstEventTimestamp)"
        prepend-icon="mdi-clock-start"
      />
      <v-list-item
        v-if="!isTimestampIncident && incident.state === 'ended'"
        :title="$t('incidents.barnDocumentation.titles.endTimestamp')"
        :subtitle="humanReadableTimestamp(incident.lastEventTimestamp)"
        prepend-icon="mdi-clock-end"
      />

      <v-list-item
        v-if="isGlobal"
        :title="$t('incidents.barnDocumentation.titles.location')"
        :subtitle="$t('incidents.barnDocumentation.global')"
        prepend-icon="mdi-earth"
      />
      <v-list-item
        v-if="hasCameras"
        :title="$t('incidents.barnDocumentation.titles.cameras')"
        :subtitle="humanReadableCameras"
        prepend-icon="mdi-video"
      />
      <v-list-item
        v-if="hasRegions"
        :title="$t('incidents.barnDocumentation.titles.regions')"
        :subtitle="humanReadableRegions"
        prepend-icon="mdi-map-marker"
      />
      <v-list-item
        v-if="wasCreatedAutomatically"
        :title="$t('incidents.barnDocumentation.titles.created_automatically')"
        :subtitle="$t('incidents.barnDocumentation.created_automatically')"
        prepend-icon="mdi-robot"
      />
      <v-list-item
        v-if="!wasCreatedAutomatically"
        :title="$t('incidents.barnDocumentation.titles.creator')"
        :subtitle="incident.triggeredBy.displayName"
        prepend-icon="mdi-account"
      />

      <v-list-item
        v-if="incident.hidden === true"
        :title="$t('incidents.barnDocumentation.titles.hideReason')"
        prepend-icon="mdi-file-question"
      >
        <WYSIWYG
          :model-value="incident.hiddenNote"
          :read-only="true"
          hide-details
        />
      </v-list-item>

      <v-list-item
        v-if="!isDescriptionEmpty"
        :title="$t('incidents.barnDocumentation.titles.description')"
        prepend-icon="mdi-text-box"
        class="my-2"
      >
        <WYSIWYG
          :model-value="incident.messagePayload.description"
          :read-only="true"
          hide-details
        />
      </v-list-item>
    </v-card-text>
    <v-card-actions class="justify-end">
      <slot
        name="genericActions"
        v-bind="{ enableNewEventButton: incident.state !== 'ended', newEventButtonText: newEventButtonText, enableHideAndShowButtons: true }"
      />
    </v-card-actions>
  </v-layout-card>
</template>

<style scoped></style>
